import React from 'react';

import { PartnerRegistrationManagerLayoutComponent } from 'design/partnerRegistrationManagerLayout';
import { useSignedIn } from 'hooks/useSignedIn';
import { PartnerRegistrationManagerAuthForm } from 'containers/PartnerRegistrationManager/PartnerRegistrationManagerAuthForm';
import { PartnerRegistrationManagerVerificationForm } from 'containers/PartnerRegistrationManager/PartnerRegistrationManagerVerificationForm';
import { PartnerRegistrationManagerProfileForm } from 'containers/PartnerRegistrationManager/PartnerRegistrationManagerProfileForm';
import { PartnerRegistrationManagerPasswordForm } from 'containers/PartnerRegistrationManager/PartnerRegistrationManagerPasswordForm';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { ThemeProviderComponent } from 'design/themeProvider';
import { useUserRoles } from 'hooks/useUserRole';
import { Redirect } from 'react-router';
import { useUserAuthProvider } from 'hooks/useUserAuthProvider';
import { TUserAccountInfo } from 'types/state';

export const PartnerRegistrationManager = () => {
  const [partnerVerified, setPartnerVerified] = React.useState(false);

  const [passwordIsSet, setPasswordIsSet] = React.useState(false);

  const signedIn = useSignedIn();

  const userRoles = useUserRoles();

  const userAuthProvider = useUserAuthProvider();

  const dispatch = useAppDispatcher();

  const userHasPartnerCandidateRole = userRoles.includes('PARTNER_CANDIDATE');

  const userAuthorizedWithGoogle = userAuthProvider === 'GOOGLE';

  const handleVerificationFormDidSucceed = React.useCallback(
    (accountInfo: void | TUserAccountInfo) => {
      setPartnerVerified(true);

      dispatch({
        name: 'PartnerRegistrationManager__VerificationFormSubmitDidSucceed',
        accountInfo,
      });
    },
    [dispatch]
  );

  const handlePasswordFormSubmitDidSucceed = React.useCallback(() => {
    setPasswordIsSet(true);

    dispatch({
      name: 'PartnerRegistrationManager__PasswordFormSubmitDidSucceed',
    });
  }, [dispatch]);

  const handleProfileFormSubmitDidSucceed = React.useCallback(() => {
    dispatch({
      name: 'PartnerRegistrationManager__ProfileFormSubmitDidSucceed',
    });
  }, [dispatch]);

  const content = React.useMemo(() => {
    if (!partnerVerified && !userHasPartnerCandidateRole) {
      return (
        <PartnerRegistrationManagerVerificationForm
          onSubmitDidSucceed={handleVerificationFormDidSucceed}
        />
      );
    }

    if (!signedIn) {
      return <PartnerRegistrationManagerAuthForm />;
    }

    if (userHasPartnerCandidateRole) {
      if (!passwordIsSet && !userAuthorizedWithGoogle) {
        return (
          <PartnerRegistrationManagerPasswordForm
            onSubmitDidSucceed={handlePasswordFormSubmitDidSucceed}
          />
        );
      }

      return (
        <PartnerRegistrationManagerProfileForm
          onSubmitDidSucceed={handleProfileFormSubmitDidSucceed}
        />
      );
    }

    return <Redirect to="/" />;
  }, [
    partnerVerified,
    userHasPartnerCandidateRole,
    signedIn,
    handleVerificationFormDidSucceed,
    passwordIsSet,
    userAuthorizedWithGoogle,
    handleProfileFormSubmitDidSucceed,
    handlePasswordFormSubmitDidSucceed,
  ]);

  return (
    <ThemeProviderComponent mode="onDark">
      <PartnerRegistrationManagerLayoutComponent content={content} />
    </ThemeProviderComponent>
  );
};
